import { SetStateAction, Dispatch, useState } from 'react'
import generalClasses from '../tabs/styles.module.scss'
import { IInfrastructureProjectObject } from '../../../types/project.types'
import ReactSelect from 'react-select';
import { findOptionByValue, t } from '../../../helpers/utils';
import Field from '../../../components/field/field.component';
import { PlusIcon } from '../../../assets/icons';

interface IProps {
    data: IInfrastructureProjectObject;
    setData: Dispatch<SetStateAction<IInfrastructureProjectObject>>;
    lang: 'ru' | 'kz';
    newFund: { labelRu: string; labelKz: string; value: string } | null;
    setNewFund: Dispatch<SetStateAction<{ labelRu: string; labelKz: string; value: string }>>;
    isAddingFund: boolean;
    setIsAddingFund: Dispatch<SetStateAction<boolean>>;
    parentErrors: Record<string, boolean>;
}

const translationKey = 'projects.infrastructure-projects';

const Documentation = ({
    data,
    setData,
    lang,
    newFund,
    setNewFund,
    isAddingFund,
    setIsAddingFund,
    parentErrors,
}: IProps) => {
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const suffix = lang === 'ru' ? 'Ru' : 'Kz';

    const documentationStatusOptions = [
        { label: t(`${translationKey}.HAVE`, lang), value: 'HAVE' },
        { label: t(`${translationKey}.IN_PROCESS`, lang), value: 'IN_PROCESS' },
        { label: t(`${translationKey}.NOT_HAVE`, lang), value: 'NOT_HAVE' },
    ];

    const handleInputChange = (field: string, value: any) => {
        setData(prev => ({
            ...prev,
            infrastructureEntity: {
                ...prev.infrastructureEntity,
                [field]: value
            }
        }));
    };

    const handleFundChange = (key: string, field: 'labelRu' | 'labelKz' | 'value', value: string) => {
        setData(prev => {
            const existing = prev.infrastructureEntity.requiredFund?.[key] ?? {
                labelRu: '',
                labelKz: '',
                value: ''
            };

            return {
                ...prev,
                infrastructureEntity: {
                    ...prev.infrastructureEntity,
                    requiredFund: {
                        ...prev.infrastructureEntity.requiredFund,
                        [key]: {
                            ...existing,
                            [field]: value
                        }
                    }
                }
            };
        });
    };


    const startAddFund = () => {
        setNewFund({ labelRu: '', labelKz: '', value: '' });
        setIsAddingFund(true);
    };

    const cancelAddFund = () => {
        setIsAddingFund(false);
        setNewFund({ labelRu: '', labelKz: '', value: '' });
    };

    const saveNewFund = () => {
        const errors: Record<string, boolean> = {};
        if (!newFund) return;

        ['labelRu', 'labelKz', 'value'].forEach(field => {
            if (!newFund[field as keyof typeof newFund]) {
                errors[field] = true;
            }
        });

        setErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setData(prev => ({
            ...prev,
            infrastructureEntity: {
                ...prev.infrastructureEntity,
                requiredFund: {
                    ...prev.infrastructureEntity.requiredFund,
                    [`created-${Date.now()}`]: { ...newFund }
                }
            }
        }));

        cancelAddFund();
    };

    if (!data) return null;

    return (
        <div className={`${generalClasses[`field-block`]} ${generalClasses[`field-block--gray`]}`}>
            <div className={`${generalClasses['field-block__label']} ${generalClasses['required']}`}>
                {t(`${translationKey}.documentation-status`, lang)}
            </div>

            <ReactSelect
                options={documentationStatusOptions}
                value={findOptionByValue(data.infrastructureEntity.documentationStatus as string | null, documentationStatusOptions)}
                onChange={(option) => handleInputChange('documentationStatus', option?.value || '')}
                placeholder=""
                className={generalClasses['react-select']}
            />

            {
                data.infrastructureEntity.documentationStatus === 'HAVE' && (
                    <>
                        <Field
                            label={t(`${translationKey}.workStart`, lang)}
                            value={data.infrastructureEntity?.workStart || ''}
                            onChange={(e) => handleInputChange('workStart', e)}
                            required
                            type="date"
                            error={parentErrors.workStart}
                        />
                        <Field
                            label={t(`${translationKey}.workEnd`, lang)}
                            type="date"
                            value={data.infrastructureEntity?.workEnd || ''}
                            onChange={(e) => handleInputChange('workEnd', e)}
                            required
                            error={parentErrors.workEnd}
                        />
                        <Field
                            label={t(`${translationKey}.requiredFundSource`, lang)}
                            type="text"
                            value={data.infrastructureEntity?.[`requiredFundSource${suffix}`] || ''}
                            onChange={(e) => handleInputChange(`requiredFundSource${suffix}`, e)}
                            required
                            error={parentErrors[`requiredFundSource${suffix}`]}
                        />
                        <Field
                            label={t(`${translationKey}.requiredFundAmount`, lang)}
                            type="number"
                            value={data.infrastructureEntity?.requiredFundAmount || ''}
                            onChange={(e) => handleInputChange(`requiredFundAmount`, e)}
                            required
                            error={parentErrors.requiredFundAmount}
                        />

                        <div className={generalClasses['field-block__title']}>
                            {t(`${translationKey}.fundSources`, lang)}
                        </div>

                        {data.infrastructureEntity.requiredFund && Object.entries(data.infrastructureEntity.requiredFund).map(([key, value]) => (
                            <Field
                                label={value[`label${suffix}`] || ''}
                                value={value.value}
                                onChange={(val) => handleFundChange(key, 'value', val.toString())}
                                required
                                type="number"
                                key={key}
                                error={parentErrors[`requiredFund.${key}-value`]}
                            />
                        ))}

                        {!isAddingFund && (
                            <div className={generalClasses['field-block__actions']}>
                                <button onClick={startAddFund}>
                                    <PlusIcon /> {t(`actions.add`, lang)}
                                </button>
                            </div>
                        )}

                        {isAddingFund && newFund && (
                            <div className={generalClasses['field-block']}>
                                <Field
                                    label={t(`${translationKey}.label`, lang)}
                                    value={newFund[`label${suffix}`] || ''}
                                    onChange={(val) => setNewFund(prev => ({ ...prev, [`label${suffix}`]: val.toString() }))}
                                    error={errors[`label${suffix}`]}
                                    required
                                    type="text"
                                />
                                <Field
                                    label={t(`${translationKey}.value`, lang)}
                                    value={newFund.value}
                                    onChange={(val) => setNewFund(prev => ({ ...prev, value: val.toString() }))}
                                    error={errors.value}
                                    required
                                    type="number"
                                />
                                <div className={generalClasses['field-block__actions']}>
                                    <button onClick={saveNewFund}>{t(`actions.save`, lang)}</button>
                                    <button onClick={cancelAddFund}>{t(`actions.cancel`, lang)}</button>
                                </div>
                            </div>
                        )}
                    </>
                )
            }
        </div>
    );
};

export default Documentation;
